import { useNavigate, useParams } from "react-router-dom";
import { SwitchPrimary } from "shared/components/SwitchPrimary";
import { MainTooltip } from "shared/components/MainTooltip";
import { Button } from "shared/components/Button";
import { VerticalCounter } from "shared/components/VerticalCounter";
import { useTranslation } from "hooks/useTranslation";
import styles from "./OptionRowItem.module.scss";
import {
  IconInfo,
  IconMonitor,
  IconFan,
  IconHeater,
  IconSiren,
  IconTopPreHeat,
  IconTypeCnArrowLoop,
  IconBottomPreHeat,
  IconTypeCnArrowLoopReverse,
  IconFluxHead,
  IconFiducialCorrection,
  IconBoardMaquette,
  IconBarcodeReader,
  IconElectromagneticPump,
  IconThermalNozzle,
  IconCenterPoint,
  IconStreamLineLocation,
  IconStreamLineTarget,
} from "icons";
import IconFlowMeter from "assets/icons/icon-flow-meter.png";

const iconsByNameWhite = {
  iconMonitor: <IconMonitor color={"#fff"} />,
  iconFan: <IconFan color={"#fff"} />,
  iconHeater: <IconHeater color={"#fff"} />,
  iconSiren: <IconSiren color={"#fff"} />,
};

export const OptionRowItem = props => {
  const { translate } = useTranslation();
  const {
    icon = false,
    disabled,
    name,
    description,
    forwardTo,
    empty,
    checked,
    onToggle,
    tooltipContentRef,
    isEditable,
    count,
    handleClickOptionShowMore = () => {},
    handleIncrementOptionCount,
    handleDecrementOptionCount,
  } = props;

  const { company = "general" } = useParams();

  const navigate = useNavigate();

  const iconByName = {
    iconMonitor: <IconMonitor color={!empty ? "#000" : undefined} />,
    iconFan: <IconFan color={!empty ? "#000" : undefined} />,
    iconHeater: <IconHeater color={!empty ? "#000" : undefined} />,
    iconSiren: <IconSiren color={!empty ? "#000" : undefined} />,
    iconTopPreHeat: <IconTopPreHeat color={!empty ? "#000" : undefined} />,
    iconTypeCnArrowLoop: (
      <IconTypeCnArrowLoop color={!empty ? "#000" : undefined} />
    ),
    iconBottomPreHeat: (
      <IconBottomPreHeat color={!empty ? "#000" : undefined} />
    ),
    iconTypeCnArrowLoopReverse: (
      <IconTypeCnArrowLoopReverse color={!empty ? "#000" : undefined} />
    ),
    iconFluxHead: <IconFluxHead color={!empty ? "#000" : undefined} />,
    iconFiducialCorrection: (
      <IconFiducialCorrection color={!empty ? "#000" : undefined} />
    ),
    iconBoardMaquette: (
      <IconBoardMaquette color={!empty ? "#000" : undefined} />
    ),
    iconFlowMeter: (
      <img width={48} height={48} src={IconFlowMeter} alt="flow-meter" />
    ),
    iconBarcodeReader: (
      <IconBarcodeReader color={!empty ? "#000" : undefined} />
    ),
    iconElectromagneticPump: (
      <IconElectromagneticPump color={!empty ? "#000" : undefined} />
    ),
    iconStreamLineTarget: (
      <IconStreamLineTarget color={!empty ? "#000" : undefined} />
    ),
    iconStreamLineLocation: (
      <IconStreamLineLocation color={!empty ? "#000" : undefined} />
    ),
    iconCenterPoint: <IconCenterPoint color={!empty ? "#000" : undefined} />,
    iconThermalNozzle: (
      <IconThermalNozzle color={!empty ? "#000" : undefined} />
    ),
  };

  const parseForwardTo = forwardTo => {
    const url = new URL(forwardTo, window.location.origin);
    const pathname = url.pathname;
    const searchParams = url.searchParams;

    const forwardToObj = {
      page: pathname.split("/")[1]?.toLowerCase() || "",
      name: searchParams.get("name")?.toLowerCase() || "",
      item: searchParams.get("option")?.toLowerCase() || "",
    };

    return forwardToObj;
  };

  const forwardToObj = parseForwardTo(forwardTo);

  const forwardLinkObj = forwardToObj && {
    pathname: `/${company}/${forwardToObj.page}`,
    state: { item: forwardToObj.item, scrollToSwiper: true },
    search: `?name=${encodeURIComponent(forwardToObj.name)}`,
  };

  const handleToggle = event => {
    if (onToggle) {
      onToggle(event.target.checked);
    }
  };

  const handleClickItem = () => {
    navigate(`/${company}/${forwardTo}`);
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftSec}>
        {icon && iconByName[icon]}
        {empty ? <span className={styles.rowLine}></span> : name}
      </div>
      <div className={styles.rightSec}>
        <MainTooltip
          title={
            <div ref={tooltipContentRef} className={styles.tooltipContent}>
              {icon && iconsByNameWhite[icon]}
              <h6 className={styles.title}>{name}</h6>
              <p className={styles.description}>{description || "..."}</p>
              <Button
                onClick={() => {
                  if (forwardTo) {
                    handleClickOptionShowMore(forwardTo);
                  }
                }}
                type={"transparentOutlined"}
                size="sm"
                disabled={!forwardTo}
              >
                {translate("Show More")}
              </Button>
            </div>
          }
          color="#000000CC"
          padding="16px"
          fontSize="14px"
          fontWeight="400"
          borderRadius="8px"
          placement="top"
        >
          <div
            className={`${styles.infoIconContainer} ${
              disabled && empty && "noPointerEvents"
            }`}
          >
            <IconInfo color={!empty ? "#008ECC" : undefined} />
          </div>
        </MainTooltip>
        <SwitchPrimary
          onChange={handleToggle}
          checked={checked}
          disabled={disabled || !isEditable}
          isEditable={isEditable}
        />
        <VerticalCounter
          outerCount={count}
          handleIncrement={handleIncrementOptionCount}
          handleDecrement={handleDecrementOptionCount}
          isDisabledActions={!checked || !isEditable}
        />
      </div>
    </div>
  );
};
